import React, { useEffect, useMemo, useState, lazy, Suspense } from 'react';
import styled from 'styled-components/macro';
import { Helmet } from 'react-helmet-async';
import api from '../../Middleware/api/api';
import { useAuth } from '../../services/contexts';
import { useUser } from '../../services/contexts/UserContext';
import { useTranslation } from 'react-i18next';
import { Field, Form, Formik } from 'formik';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { format } from 'date-fns';
import shadows from '@mui/material/styles/shadows';
import dayjs from 'dayjs';
import * as Yup from 'yup';
import Box from '@mui/system/Box';
import IconButton from '@mui/material/IconButton';
import FileOpen from '@mui/icons-material/FileOpenOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import { formatCurrency } from '../../services/utils/format-numbers';
const Feedback = lazy(() => import('../../components/DataTable/Feedback'));
import BillStatusLabel from '../../components/BillStatusLabel';
import LoadingButton from '../../components/core/LoadingButton';
import Tooltip from '@mui/material/Tooltip';
import {
  Divider as MuiDivider,
  Grid,
  Button,
  Stack,
  TableCell,
  Typography,
  TextField,
  Paper,
  Menu,
  MenuItem,
  ThemeProvider,
} from '@mui/material';
import { spacing } from '@mui/system';
import { createTheme } from '@mui/material/styles';
import LogoLoader from '../../components/LogoLoader';
import DataTable from '../../components/DataTable';
import LightTooltip from '../Users/components/CreateUser/LightTooltip';
import { Item } from './styles';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import {
  PAGE_SIZE,
  BillingDataForOrigin,
  DEFAULT_CURRENT_INIT_PAGE,
} from '../../services/utils/units-constants';
import jwtDecode from 'jwt-decode';
import {
  getUserTypeData,
  getPaymentMethodsData,
  getBillStatusData,
} from '../../services/utils/translations-constants.js';

import { useDropdown } from '../../services/hooks/DropDownStateHooks';

const Divider = styled(MuiDivider)(spacing);
let rows = [];

// eslint-disable-next-line no-unused-vars

function getStartDate() {
  let date = new Date();
  let dayOfMonth = date.getDate();
  return calculatedates(date, dayOfMonth - 1);
}

function getEndDate() {
  let date = new Date();
  return calculatedates(date, 0);
}

function calculatedates(date, minusDays) {
  let last = new Date(date.getTime() - minusDays * 24 * 60 * 60 * 1000);
  return (
    last.getFullYear() +
    '-' +
    last.toLocaleString('en-US', { month: '2-digit' }) +
    '-' +
    last.toLocaleString('en-US', { day: '2-digit' })
  );
}

function OperatorBills() {
  const { i18n, t } = useTranslation();
  const [dropDownForCpoData, setDropDownForCpoData] = useState([]);
  const [userTypeMap, setUserTypeMap] = useState(
    getUserTypeData(t).userTypeMap
  );
  const [paymentMethodsMap, setPaymentMethodsMap] = useState(
    getPaymentMethodsData(t).paymentMethodsMap
  );
  const [billingStatusesMap, setBillingStatusesMap] = useState(
    getBillStatusData(t).billingStatusesMap
  );

  let initDataSuspenseFallbackMessage = {
    open: false,
    title: '',
    severity: '',
    txtMessage: '',
    fetchBillsForOperator: true,
    logic: '',
  };

  const dataTableTheme = (appTheme) =>
    createTheme({
      components: {
        MuiList: {
          styleOverrides: {
            root: {
              maxHeight: 300,
            },
          },
        },
        MuiPaper: {
          variants: [
            {
              props: {
                variant: 'elevation',
              },
              style: {
                boxShadow: shadows[3],
              },
            },
          ],
        },
        MUIDataTableFilter: {
          styleOverrides: {
            root: {
              width: 300,
              boxShadow: shadows[3],
              backgroundColor: appTheme.palette.background.paper,
            },
            title: {
              lineHeight: 3,
            },
            header: {
              marginBottom: 0,
            },
            gridListTile: {
              styleOverrides: {
                paddingLeft: '25px',
                marginLeft: '25px',
              },
              sx: {
                pl: 28,
              },
            },

            reset: {
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              paddingRight: 4,
            },
            resetLink: {
              button: {
                variants: [
                  {
                    props: {
                      variant: 'outlined',
                    },
                  },
                ],
              },
            },
          },
        },
        MUIDataTableHeadCell: {
          styleOverrides: {
            root: {
              paddingLeft: '8px',
              '&[data-colindex="5"], &[data-colindex="8"]': {
                '& > *': {
                  display: 'flex',
                  justifyContent: 'center',
                  textAlign: 'center',
                },
                '& .MuiButton-root': {
                  margin: 'auto',
                },
                '&[data-colindex="4"], &[data-colindex="1"], &[data-colindex="2"], &[data-colindex="3"], &[data-colindex="4", &[data-colindex="6"]':
                  {
                    '& > *': {
                      display: 'flex',
                      justifyContent: 'left',
                      textAlign: 'left',
                    },
                    '& .MuiButton-root': {
                      margin: 'auto',
                      paddingLeft: '8px',
                    },
                  },
                '&[data-colindex="7"]': {
                  '& > *': {
                    display: 'flex',
                    justifyContent: 'right',
                    textAlign: 'right',
                  },
                  '& .MuiButton-root': {
                    margin: 'auto',
                  },
                },
              },
            },
          },
        },
        MuiFormControl: {
          defaultProps: {
            variant: 'filled',
            margin: 'dense',
            size: 'medium',
            fullWidth: false,
            sx: {
              ml: 2,
              width: '230px',
            },
          },
        },
        MuiSelect: {
          defaultProps: {
            margin: 'dense',
          },
        },
      },
    });

  const [startDate, setStartDate] = useState(getStartDate());
  const [endDate, setEndDate] = useState(getEndDate());
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loadingPDF] = useState([]);
  const [loading, setLoading] = useState(false);
  const [operatorResponse] = useState(null);
  const [operatorRequestError] = useState(null);
  const [showBetreiberButton] = useState(true);
  const [BTNSubmitIsSubmitting, setBTNSubmitIsSubmitting] = useState(false);
  const [ButtonSendBillsIsSubmitting, setButtonSendBillsIsSubmitting] =
    useState(false);
  const [currentPage, setCurrentPage] = useState(DEFAULT_CURRENT_INIT_PAGE);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE[0]);
  //standort states
  const { token } = useAuth();
  const { user } = useUser();
  const [bills, setBills] = useState([]);
  const [enterKeyboardButtonPressed, setEnterKeyboardButtonPressed] =
    useState(false);
  const [openTooltipId, setOpenTooltipId] = useState(null);
  const open = Boolean(anchorEl);
  const [
    ButtonRegenerateIncorrectBillsIsSubmitting,
    setButtonRegenerateIncorrectBillsIsSubmitting,
  ] = useState(false);

  const [dataSuspenseFallbackMessage, setDataSuspenseFallbackMessage] =
    useState(initDataSuspenseFallbackMessage);

  const {
    list: billingStatusesCpoList,
    map: billingStatusesCpoMap,
    setDropdown: setBillingStatuses,
  } = useDropdown();

  const {
    list: paymentMethodsCpoList,
    map: paymentMethodsCpoMap,
    setDropdown: setPaymentMethods,
  } = useDropdown();

  const {
    list: userTypesForCpoList,
    map: userTypesForCpoMap,
    setDropdown: setUserTypes,
  } = useDropdown();

  const handleIConClick = (billId) => {
    setOpenTooltipId(billId);
  };

  const handleTooltipClose = () => {
    setOpenTooltipId(null);
  };

  const handleTooltipOpen = (billId) => {
    setOpenTooltipId(billId);
  };

  const unselectAllRows = () => {
    setSelectedRows([]);
    setSelected([]);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const [newFilters, setNewFilters] = useState([]);

  const buildUrlParams = useMemo(() => {
    const urlParams = new URLSearchParams();
    if (newFilters && typeof newFilters === 'object') {
      Object.entries(newFilters).forEach(([key, values]) => {
        if (Array.isArray(values) && values.length > 0) {
          let formattedValues = values;
          if (key === 'userTypes') {
            formattedValues = values.map(
              (label) => userTypesForCpoMap[label] || label
            );
          }
          if (key === 'paymentMethods') {
            formattedValues = values.map(
              (label) => paymentMethodsCpoMap[label] || label
            );
          }
          if (key === 'billingStatuses') {
            formattedValues = values.map(
              (label) => billingStatusesCpoMap[label] || label
            );
          }
          urlParams.set(key, formattedValues.join(','));
        }
      });
    }
    urlParams.set('startDate', startDate + ' 00:00');
    urlParams.set('endDate', endDate + ' 23:59');
    urlParams.set('origin', BillingDataForOrigin.BILLS_PAGE);

    urlParams.set('page', currentPage);
    urlParams.set('size', pageSize);
    return urlParams;
  }, [newFilters, currentPage, pageSize, startDate, endDate]);

  useEffect(() => {
    if (token && user) {
      fetchBillsForDashboard();
    }
  }, [
    token,
    operatorResponse,
    operatorRequestError,
    showBetreiberButton,
    user,
    currentPage,
    pageSize,
  ]);
  useEffect(() => {
    if (token && user) {
      fetchConfigurationsDropDownForCpo();
    }
  }, [token, user]);

  const fetchBillsForDashboard = async () => {
    fetchBills(buildUrlParams);
  };

  const resetFilterApi = () => {
    const resetParams = new URLSearchParams();
    resetParams.set('startDate', startDate + ' 00:00');
    resetParams.set('endDate', endDate + ' 23:59');
    resetParams.set('origin', BillingDataForOrigin.BILLS_PAGE);
    resetParams.set('page', '0');
    resetParams.set('size', pageSize.toString());
    fetchBills(resetParams);
  };

  const fetchBills = async (urlParams) => {
    if (token && user) {
      try {
        setLoading(true);
        unselectAllRows();
        setBTNSubmitIsSubmitting(true);
        api.fetchBills(token, urlParams).then(
          (response) => {
            if (
              response?.data?.data == null ||
              response?.data?.data?.response === null
            ) {
              setLoading(false);
              setBTNSubmitIsSubmitting(false);
              rows = [];
              setBills(rows);
            } else {
              setTotalRecords(response?.data?.data?.totalNumberOfRecords);
              setLoading(false);
              setBTNSubmitIsSubmitting(false);
              rows = response?.data?.data?.response?.map((item) => ({
                ...item,
                fullName:
                  `${item.endCustomerFirstName ?? ''} ${item.endCustomerLastName ?? ''}`.trim(),
              }));
              setBills(rows);
            }
          },
          () => {
            setLoading(false);
            setBTNSubmitIsSubmitting(false);
            rows = [];
            setBills(rows);
          }
        );
      } catch (exception) {
        setLoading(false);
        setBTNSubmitIsSubmitting(false);
        rows = [];
        setBills(rows);
      }
    }
  };

  const fetchConfigurationsDropDownForCpo = async () => {
    try {
      const response = await api.fetchConfigurationsDropDownForCpo(token);
      const data = response?.data?.data;

      if (!Array.isArray(data) || data.length === 0) {
        console.error('No dropdown config data');
        return;
      }
      setDropDownForCpoData(data);
      handleDropDown(
        data,
        'BILLING_STATUS',
        billingStatusesMap,
        setBillingStatuses
      );
      handleDropDown(
        data,
        'PAYMENT_METHOD',
        paymentMethodsMap,
        setPaymentMethods
      );
      handleDropDown(data, 'USER_TYPE', userTypeMap, setUserTypes);
    } catch (err) {
      console.error('Failed to fetch dropdown configs:', err);
    }
  };

  useEffect(() => {
    if (Array.isArray(dropDownForCpoData) && dropDownForCpoData?.length > 0) {
      let userTypeMap = getUserTypeData(t).userTypeMap;
      let paymentMethodsMap = getPaymentMethodsData(t).paymentMethodsMap;
      let billingStatusesMap = getBillStatusData(t).billingStatusesMap;

      setUserTypeMap(userTypeMap);
      setPaymentMethodsMap(paymentMethodsMap);
      setBillingStatusesMap(billingStatusesMap);

      handleDropDown(
        dropDownForCpoData,
        'BILLING_STATUS',
        billingStatusesMap,
        setBillingStatuses
      );
      handleDropDown(
        dropDownForCpoData,
        'PAYMENT_METHOD',
        paymentMethodsMap,
        setPaymentMethods
      );
      handleDropDown(
        dropDownForCpoData,
        'USER_TYPE',
        userTypeMap,
        setUserTypes
      );
    }
  }, [i18n.language]);

  const handleDropDown = (data, type, labelMap, setDropdown) => {
    const item = data.find((entry) => entry.type === type);
    if (item?.listValues) {
      setDropdown(item.listValues, labelMap);
    }
  };

  const decodedToken = token ? jwtDecode(token) : null;

  const renderToHtml = (props) => {
    return (
      <div
        dangerouslySetInnerHTML={{
          __html: props,
        }}
      />
    );
  };

  const renderPaymentIntents = (props) => {
    return renderToHtml(
      showstripeSepaPaymentIntentIdAndStripeBillPaymentStatus(props)
    );
  };

  const getBillCollectionDate = (props) => {
    return props.billCollectionDate != null
      ? new Date(props.billCollectionDate).toLocaleDateString()
      : t('LBLBillNotYetSent');
  };
  const getStripePaymentIntent = (props) => {
    return props.stripeSepaPaymentIntentId != null
      ? props.stripeSepaPaymentIntentId
      : '';
  };
  const getStripePaymentStatus = (props) => {
    return props.stripeSepaBillPaymentStatus != null
      ? props.stripeSepaBillPaymentStatus
      : '';
  };

  function showstripeSepaPaymentIntentIdAndStripeBillPaymentStatus(props) {
    let billCollectionDateSection = '';
    billCollectionDateSection =
      t('LBLBillCollectionDate') + ' : ' + getBillCollectionDate(props);
    let paymentIntentSection = '';
    paymentIntentSection =
      '<br>' +
      t('stripeSepaPaymentIntentId') +
      ' : ' +
      getStripePaymentIntent(props);
    let paymentStatusSection = '';
    paymentStatusSection =
      '<br>' +
      t('stripeSepaPaymentStatus') +
      ' : ' +
      getStripePaymentStatus(props);
    return (
      billCollectionDateSection + paymentIntentSection + paymentStatusSection
    );
  }

  const columns = useMemo(
    () => [
      {
        name: 'billNumber',
        key: 'billNumberCheckbox',
        options: {
          display: false,
          filter: false,
          customBodyRender: (value) => {
            return value || <>-</>;
          },
        },
      },
      {
        name: 'billNumber',
        key: 'billNumber',
        label: t('billnum'),
        options: {
          filter: true,
          filterOptions: {
            fullWidth: true,
          },
          sort: false,
          customFilterListRender: (v) => t('billnum') + `: ${v}`,
          filterType: 'textField',
          customHeadRender: () => (
            <TableCell
              align="left"
              variant="head"
              sx={{
                textAlign: 'center',
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
              }}
            >
              {t('billnum')}
            </TableCell>
          ),
          customBodyRender: (value) => (
            <Typography align="center">{value}</Typography>
          ),
        },
      },
      {
        name: 'endCustomerId',
        key: 'endCustomerId',
        label: t('LBLendCustomerId'),
        options: {
          display: true,
          filter: true,
          filterOptions: {
            fullWidth: true,
          },
          sort: false,
          customFilterListRender: (v) => t('LBLendCustomerId') + `: ${v}`,
          filterType: 'textField',
          customHeadRender: () => (
            <TableCell
              align="left"
              variant="head"
              sx={{
                textAlign: 'center',
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
              }}
            >
              {t('LBLendCustomerId')}
            </TableCell>
          ),
          customBodyRender: (value) => (
            <Typography align="center">{value}</Typography>
          ),
        },
      },
      {
        name: 'fullName',
        key: 'fullName',
        label: t('LBLfullName'),
        options: {
          display: true,
          filter: true,
          filterOptions: {
            fullWidth: true,
          },
          sort: false,
          customFilterListRender: (v) => t('LBLfullName') + `: ${v}`,
          filterType: 'textField',
          customHeadRender: () => (
            <TableCell
              align="left"
              variant="head"
              sx={{
                textAlign: 'left',
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
                maxWidth: '6vw', // Adjust this percentage for responsiveness
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {t('LBLfullName')}
            </TableCell>
          ),
          customBodyRender: (value) => (
            <Tooltip title={value || ''} placement="top">
              <Typography
                align="left"
                sx={{
                  maxWidth: '6vw', // Matches header width, responsive to screen size
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {value}
              </Typography>
            </Tooltip>
          ),
        },
      },
      {
        name: 'userType',
        key: 'userType',
        label: t('LBLUserType'),
        options: {
          filter: false,
          customHeadRender: () => (
            <TableCell
              align="left"
              variant="head"
              sx={{
                textAlign: 'left',
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
                maxWidth: '6vw', // Adjust this percentage for responsiveness
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {t('LBLUserType')}
            </TableCell>
          ),
          customBodyRender: (userType) => {
            const translatedUserType = userTypeMap[userType] || userType;
            return (
              <>
                <Tooltip title={translatedUserType} placement="top">
                  <Typography
                    align="left"
                    sx={{
                      maxWidth: '6vw', // Matches header width, responsive to screen size
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {translatedUserType}
                  </Typography>
                </Tooltip>
              </>
            );
          },
        },
      },
      {
        name: 'userTypes',
        label: t('LBLUserType'),
        options: {
          display: false,
          filter: true,
          sort: false,
          filterType: 'multiselect',
          filterOptions: {
            fullWidth: true,
            names: userTypesForCpoList.map((item) => item.label),
          },
          customFilterListRender: (v) => t('LBLUserType') + `: ${v}`,
        },
      },
      {
        name: 'paymentMethod',
        key: 'paymentMethod',
        label: t('LBLpaymentMethod'),
        options: {
          filter: false,
          customHeadRender: () => (
            <TableCell
              align="left"
              variant="head"
              sx={{
                textAlign: 'left',
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
              }}
            >
              {t('LBLpaymentMethod')}
            </TableCell>
          ),
          customBodyRender: (paymentMethod) => {
            const translatedPaymentMethod =
              paymentMethodsMap[paymentMethod] || paymentMethod;
            return (
              <>
                <Tooltip title={translatedPaymentMethod} placement="top">
                  <Typography
                    align="left"
                    sx={{
                      maxWidth: '6vw',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {translatedPaymentMethod}
                  </Typography>
                </Tooltip>
              </>
            );
          },
        },
      },
      {
        name: 'paymentMethods',
        label: t('LBLpaymentMethod'),
        options: {
          display: false,
          filter: true,
          sort: false,
          filterType: 'multiselect',
          filterOptions: {
            fullWidth: true,
            names: paymentMethodsCpoList.map((item) => item.label),
          },
          customFilterListRender: (v) => t('LBLpaymentMethod') + `: ${v}`,
        },
      },
      {
        name: 'billingStatus',
        key: 'billingStatus',
        label: t('LBLbillingStatus'),
        options: {
          filter: false,
          customHeadRender: () => (
            <TableCell
              align="left"
              variant="head"
              sx={{
                textAlign: 'left',
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
              }}
            >
              {t('LBLbillingStatus')}
            </TableCell>
          ),
          customBodyRender: (value) => {
            return <BillStatusLabel textAlign={'left'} title={value} />;
          },
        },
      },
      {
        name: 'billingStatuses',
        label: t('LBLbillingStatus'),
        options: {
          display: false,
          filter: true,
          sort: false,
          filterType: 'multiselect',
          filterOptions: {
            fullWidth: true,
            names: billingStatusesCpoList.map((item) => item.label),
          },
          customFilterListRender: (v) => t('LBLbillingStatus') + `: ${v}`,
        },
      },
      {
        name: 'billDownloadDate',
        key: 'billDownloadDate',
        label: t('downloadDate'),
        options: {
          filter: false,
          customHeadRender: () => (
            <TableCell
              align="left"
              variant="head"
              sx={{
                textAlign: 'right',
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
              }}
            >
              {t('downloadDate')}
            </TableCell>
          ),
          customBodyRender: (value) => (
            <Typography align="right">
              {value !== null
                ? format(new Date(value), 'dd.MM.yyyy, HH:mm') + ' Uhr'
                : ''}
            </Typography>
          ),
        },
      },
      {
        name: 'totalTaxedCosts',
        key: 'totalTaxedCosts',
        label: t('totalCost'),
        options: {
          filter: false,
          customHeadRender: () => (
            <TableCell
              align="left"
              variant="head"
              sx={{
                textAlign: 'right',
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
              }}
            >
              {t('totalCost')}
            </TableCell>
          ),
          customBodyRender: (value) => (
            <Typography align="right">{value}</Typography>
          ),
        },
      },
      {
        name: 'open',
        key: 'open',
        label: ' ',
        options: {
          filter: false,
          sort: false,
        },
      },

      ...(bills.some(
        (bill) =>
          bill.paymentMethod === 'STRIPE_SEPA' ||
          bill.paymentMethod === 'STRIPE_CARD'
      )
        ? [
            {
              name: 'details',
              key: 'details',
              label: ' ',
              options: {
                filter: false,
                sort: false,
                customBodyRender(value) {
                  return (
                    <>
                      {value.paymentMethod !== 'STRIPE_SEPA' &&
                      value.paymentMethod !== 'STRIPE_CARD' ? null : (
                        <>
                          <Item>
                            <Typography fontWeight="fontWeightMedium">
                              <InfoIcon
                                onClick={() => handleIConClick(value.billId)}
                                color="action"
                                fontSize="small"
                                cl="true"
                              />
                            </Typography>
                          </Item>

                          <LightTooltip
                            open={openTooltipId === value.billId}
                            onClose={handleTooltipClose}
                            placement="top-start"
                            title={renderPaymentIntents(value) || ''}
                            onOpen={() => handleTooltipOpen(value.billId)}
                          ></LightTooltip>
                        </>
                      )}
                    </>
                  );
                },
              },
            },
            {
              name: 'stripeSepaBillPaymentStatus',
              key: 'stripeSepaBillPaymentStatus',
              label: t('stripeSepaPaymentStatus'),
              options: {
                filter: false,
                display: false,
                search: true,
                customHeadRender: () => (
                  <TableCell
                    align="left"
                    variant="head"
                    sx={{
                      textAlign: 'right',
                      position: 'sticky',
                      top: 0,
                      backgroundColor: 'white',
                    }}
                  >
                    {t('stripeSepaPaymentStatus')}
                  </TableCell>
                ),
                customBodyRender: (value) => (
                  <Typography align="right">{value}</Typography>
                ),
              },
            },
          ]
        : []),
    ],
    [bills, renderPaymentIntents]
  );

  const renderData = useMemo(() => {
    const rawbill = bills;

    return rawbill
      ?.sort(
        (entry1, entry2) =>
          entry2.billGenerationDate - entry1.billGenerationDate
      )
      .map((entry) => {
        const loading = loadingPDF.includes(entry.billNumber);

        const { billingStatus, totalTaxedCosts, paymentMethod, ...rawbill } =
          entry;

        const newEntry = {
          ...rawbill,
          details: entry,
          totalTaxedCosts:
            totalTaxedCosts !== null ? formatCurrency(totalTaxedCosts) : null,
          paymentMethod: paymentMethod,
          billingStatus: billingStatus,
          open:
            billingStatus !== null && billingStatus != 'NOT_APPLICABLE' ? (
              <Box position="relative">
                <IconButton
                  data-bill-number={entry.billNumber}
                  disabled={loading}
                  aria-label={`open bill ${entry.billNumber}`}
                  href={`${window._env_.REACT_APP_BOOKING_SERVICE_API}/bookings/pdf/${entry.billNumber}?verificationId=${entry.billId}`}
                  target="_blank"
                >
                  <FileOpen />
                </IconButton>
                {loading && (
                  <CircularProgress
                    size={38}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      zIndex: 1,
                    }}
                  />
                )}
              </Box>
            ) : null,

          stripeSepaPaymentIntentId:
            entry.paymentMethod === 'STRIPE_SEPA' ||
            entry.paymentMethod === 'STRIPE_CARD'
              ? entry?.stripeSepaPaymentIntentId || ''
              : '',
          stripeSepaBillPaymentStatus:
            entry.paymentMethod === 'STRIPE_SEPA' ||
            entry.paymentMethod === 'STRIPE_CARD'
              ? entry?.stripeSepaBillPaymentStatus || ''
              : '',
        };

        return newEntry;
      });
  }, [bills, loadingPDF]);

  useEffect(() => {
    setSelected(selectedRows.map((index) => bills[index]));
  }, [selectedRows]);

  const dateFiltersInitialValues = {
    startDate: startDate,
    endDate: endDate,
  };

  const dateFiltersValidationSchema = useMemo(
    () =>
      Yup.object({
        startDate: Yup.date()
          .typeError(t('LBLInvalidDate'))
          .label(t('LBLStartDate'))
          .required(t('startDateRequired')),
        endDate: Yup.date()
          .typeError(t('LBLInvalidDate'))
          .min(Yup.ref('startDate'), t('LBLENdgStart'))
          .label(t('LBLEndDate'))
          .required(t('LBLEndDateReq')),
      }),
    []
  );

  const handleSubmitGenerationDates = (values) => {
    setStartDate(values.startDate);
    setEndDate(values.endDate);
    fetchBillsForDashboard();
  };

  const handleSendPdfBillEmail = (selected) => {
    sendPdfBillEmail(selected);
  };
  const handleDownloadPdfBills = (selected) => {
    downloadPdfBillsAsZip(selected);
  };
  const handleRegenerateBills = (selected) => {
    regenerateIncorrectBills(selected);
  };

  const handleStartDateChange = (newValue, setFieldValue) => {
    if (
      newValue instanceof Date &&
      (!isNaN(newValue.getTime()) || !isNaN(newValue.toDate()))
    ) {
      setStartDate(format(newValue, 'yyyy-MM-dd'));
      setFieldValue('startDate', format(newValue, 'yyyy-MM-dd'));
    } else if (newValue != null && !isNaN(newValue.toDate())) {
      setStartDate(format(newValue.toDate(), 'yyyy-MM-dd'));
      setFieldValue('startDate', format(newValue.toDate(), 'yyyy-MM-dd'));
    } else {
      setStartDate(newValue);
      setFieldValue('startDate', newValue);
      console.error('Invalid date input');
    }
  };

  const handleEndDateChange = (newValue, setFieldValue) => {
    if (
      newValue instanceof Date &&
      (!isNaN(newValue.getTime()) || !isNaN(newValue.toDate()))
    ) {
      setEndDate(format(newValue, 'yyyy-MM-dd'));
      setFieldValue('endDate', format(newValue, 'yyyy-MM-dd'));
    } else if (newValue != null && !isNaN(newValue.toDate())) {
      setEndDate(format(newValue.toDate(), 'yyyy-MM-dd'));
      setFieldValue('endDate', format(newValue.toDate(), 'yyyy-MM-dd'));
    } else {
      setEndDate(newValue);
      setFieldValue('endDate', newValue);
      console.error('Invalid EndDate input');
    }
  };

  const showResponseAfterSendingBill = (
    showSuccessFeedback,
    showErrorFeedback
  ) => {
    setButtonSendBillsIsSubmitting(false);
    unselectAllRows();
    if (showSuccessFeedback) {
      let successData = {
        open: true,
        title: t('LBLSendBillsPdfMailSuccess'),
        severity: 'success',
        txtMessage: t('MSGSendBillsPdfMailSuccess'),
        fetchBillsForOperator: true,
        logic: 'SendingBillSuccess',
      };
      setDataSuspenseFallbackMessage(successData);
    }
    if (showErrorFeedback) {
      let errorData = {
        open: true,
        title: t('LBLSendBillsPdfMailError'),
        severity: 'warning',
        txtMessage: t('MSGSendBillsPdfMailError'),
        fetchBillsForOperator: true,
        logic: 'SendingBillError',
      };
      setDataSuspenseFallbackMessage(errorData);
    }
  };

  const sendPdfBillEmail = (selected) => {
    if (token && user) {
      try {
        setLoading(true);
        setButtonSendBillsIsSubmitting(true);
        unselectAllRows();
        api.sendBillsPdfEmail(token, selected).then(
          () => {
            setLoading(false);
            showResponseAfterSendingBill(true, false);
          },
          () => {
            setLoading(false);
            showResponseAfterSendingBill(false, true);
          }
        );
      } catch (exception) {
        setLoading(false);
        showResponseAfterSendingBill(false, true);
      }
    }
  };

  const downloadPdfBillsAsZip = async (selected) => {
    if (token && user) {
      try {
        setLoading(true);
        unselectAllRows();

        // Transform the selected bills array to get billId and billNumber
        const billPdfDtoList = selected.map((bill) => ({
          billId: bill.billId,
          billNumber: bill.billNumber,
        }));

        const requestBody = {
          billPdfDtoList: billPdfDtoList,
        };

        // Making the API call to download the ZIP file
        const response = await api.downloadBills(token, requestBody);

        if (response?.status === 200) {
          const contentDisposition = response?.headers['content-disposition'];
          let filename = 'Rechnungen.zip'; // Default filename if none is provided

          if (contentDisposition && contentDisposition.includes('filename=')) {
            filename = contentDisposition
              .split('filename=')[1]
              .replace(/['"]/g, ''); // Extract the filename and remove quotes
          }

          // Create a Blob from the response data
          const blob = new Blob([response?.data], { type: 'application/zip' });

          // Create a link element to trigger download
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = filename; // Set the filename from the response header
          document.body.appendChild(link);
          link.click(); // Trigger download
          document.body.removeChild(link); // Clean up after download

          setLoading(false);
          fetchBillsForDashboard();
        } else {
          console.error('Error downloading file:', response?.statusText);
        }
      } catch (error) {
        setLoading(false);
        console.error('Error during file download:', error);
      }
    }
  };

  // attention, this handlings support only one bill response
  const showSuccessResponseAfterRegeneratingBills = (response) => {
    setButtonRegenerateIncorrectBillsIsSubmitting(false);
    unselectAllRows();
    let successfulBillRegenerations_length =
      response?.data?.data?.successfulBillRegenerations?.length;
    let unSuccessfulBillRegenerations_length =
      response?.data?.data?.unSuccessfulBillRegenerations?.length;
    if (
      successfulBillRegenerations_length === 0 &&
      unSuccessfulBillRegenerations_length === 0
    ) {
      let warningData = {
        open: true,
        title: t('LBLTitleRegenerateBillsWarning'),
        severity: 'warning',
        txtMessage: t('MSGRegenerateBillsNoBillWasRegenerated'),
        fetchBillsForOperator: true,
        logic: 'RegenerateBillsWarning',
      };
      setDataSuspenseFallbackMessage(warningData);
    } else if (
      successfulBillRegenerations_length === 0 &&
      unSuccessfulBillRegenerations_length > 0
    ) {
      let warningData = {
        open: true,
        title: t('LBLTitleRegenerateBillsError'),
        severity: 'warning',
        txtMessage: t('MSGRegenerateBillsBillsNotRegenerated'),
        fetchBillsForOperator: true,
        logic: 'RegenerateBillsWarning',
      };
      setDataSuspenseFallbackMessage(warningData);
    } else if (
      successfulBillRegenerations_length > 0 &&
      unSuccessfulBillRegenerations_length > 0
    ) {
      let warningData = {
        open: true,
        title: t('LBLTitleRegenerateBillsWarning'),
        severity: 'warning',
        txtMessage: renderToHtml(
          t('MSGRegenerateBillsBillsRegenerated') +
            ' <br> ' +
            response?.data?.data?.successfulBillRegenerations
              .map((item) => item.billNumber)
              .join(' <br> ') +
            ' <br> ' +
            t('MSGRegenerateBillsBillsNotRegenerated') +
            ' <br> ' +
            response?.data?.data?.unSuccessfulBillRegenerations
              .map((item) => item.billNumber)
              .join(' <br> ')
        ),
        fetchBillsForOperator: true,
        logic: 'RegenerateBillsWarning',
      };
      setDataSuspenseFallbackMessage(warningData);
    } else if (
      successfulBillRegenerations_length > 0 &&
      unSuccessfulBillRegenerations_length === 0
    ) {
      let successData = {
        open: true,
        title: t('LBLTitleRegenerateBillsSuccess'),
        severity: 'success',
        txtMessage: t('MSGRegenerateBillsBillsRegenerated'),
        fetchBillsForOperator: true,
        logic: 'RegenerateBillsSuccess',
      };
      setDataSuspenseFallbackMessage(successData);
    }
  };

  const ShowErrorResponseAfterRegeneratingBills = (error) => {
    setButtonRegenerateIncorrectBillsIsSubmitting(false);
    unselectAllRows();
    let txtMessage = t('MSGRegenerateBillsGlobalError');
    let errorDataTittle = t('LBLTitleRegenerateBillsError');
    if (
      error?.response?.data?.error?.errorCodeResponse ===
      'NO_INPUT_PROVIDED_ERROR'
    ) {
      txtMessage = t('MSGRegenerateBillsNoInputProvidedError');
      errorDataTittle = t('LBLTitleInvalidSelection');
    } else if (
      error?.response?.data?.error?.errorCodeResponse === 'INVALID_INPUT_ERROR'
    ) {
      txtMessage = t('MSGRegenerateBillsInvalidInputError');
      errorDataTittle = t('LBLTitleInvalidSelection');
    } else if (
      error?.response?.data?.error?.errorCodeResponse ===
      'BILLS_WITH_GENERATED_STATUS_ERROR'
    ) {
      txtMessage = t('MSGRegenerateBillsBillsWithGeneratedStatusError');
      errorDataTittle = t('LBLTitleInvalidSelection');
    } else if (
      error?.response?.data?.error?.errorCodeResponse ===
      'BILLING_UTILITY_ERROR'
    ) {
      txtMessage = t('MSGRegenerateBillsBillingUtilityError');
      errorDataTittle = t('LBLTitleRegenerateBillsError');
    } else {
      txtMessage = t('MSGRegenerateBillsGlobalError');
      errorDataTittle = t('LBLTitleRegenerateBillsError');
    }

    let errorData = {
      open: true,
      title: errorDataTittle,
      severity: 'warning',
      txtMessage: txtMessage,
      fetchBillsForOperator: true,
      logic: 'RegenerateBillsError',
    };
    setDataSuspenseFallbackMessage(errorData);
  };

  const regenerateIncorrectBills = (selected) => {
    if (token && user) {
      try {
        setLoading(true);
        setButtonRegenerateIncorrectBillsIsSubmitting(true);
        unselectAllRows();
        const billNumbers = selected.map((item) => item.billNumber);
        const payload = {
          billNumbers: billNumbers,
        };
        api.regenerateIncorrectBills(token, payload).then(
          (response) => {
            setLoading(false);
            showSuccessResponseAfterRegeneratingBills(response);
          },
          (error) => {
            setLoading(false);
            ShowErrorResponseAfterRegeneratingBills(error);
          }
        );
      } catch (exception) {
        setLoading(false);
        setButtonRegenerateIncorrectBillsIsSubmitting(false);
      }
    }
  };
  useEffect(() => {
    if (enterKeyboardButtonPressed) {
      handleApply();
    }
  }, [enterKeyboardButtonPressed]);

  const handleEnterKey = React.useCallback((e) => {
    if (e?.key === 'Enter') {
      e.preventDefault();
      setEnterKeyboardButtonPressed(true);
    }
  }, []);
  const handleApply = () => {
    if (currentPage > 0) {
      setCurrentPage(0);
    } else {
      fetchBillsForDashboard();
    }
    setEnterKeyboardButtonPressed(false);
  };
  return (
    <React.Fragment>
      <Helmet title="Invoices" />
      <LogoLoader loading={loading} />
      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            {t('LBLBills')}
          </Typography>
        </Grid>
      </Grid>

      <Grid
        item
        sx={{ width: '100%' }}
        xl={3}
        py={1}
        display="flex"
        justifyContent="flex-end"
      >
        <Formik
          initialValues={dateFiltersInitialValues}
          validationSchema={dateFiltersValidationSchema}
          validateOnMount={true}
          onSubmit={handleSubmitGenerationDates}
        >
          {({ errors, values, setFieldValue }) => (
            <Form noValidate>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                width="100%"
              >
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="de"
                >
                  <DatePicker
                    label={t('LBLStartDate')}
                    PaperProps={{ sx: { boxShadow: shadows[3] } }}
                    value={values.startDate}
                    onChange={(newValue) => {
                      handleStartDateChange(newValue, setFieldValue);
                    }}
                    renderInput={(params) => (
                      <Field
                        component={TextField}
                        {...params}
                        required
                        name="startDate"
                        margin="none"
                        autoComplete="off"
                        helperText={errors.startDate}
                        error={errors.startDate}
                        inputProps={{
                          ...params.inputProps,
                          placeholder: 'TT.MM.JJJJ',
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="de"
                >
                  <DatePicker
                    PaperProps={{ sx: { boxShadow: shadows[3] } }}
                    label={t('LBLEndDate')}
                    value={values.endDate}
                    onChange={(newValue) => {
                      handleEndDateChange(newValue, setFieldValue);
                    }}
                    maxDate={dayjs()}
                    renderInput={(params) => (
                      <Field
                        component={TextField}
                        {...params}
                        name="endDate"
                        margin="none"
                        autoComplete="off"
                        helperText={errors.endDate}
                        error={errors.endDate}
                        inputProps={{
                          ...params.inputProps,
                          placeholder: 'TT.MM.JJJJ',
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ px: 3, my: 4, borderRadius: 1 }}
                  disabled={errors.startDate || errors.endDate}
                  loading={BTNSubmitIsSubmitting}
                >
                  {t('BTNSubmit')}
                </LoadingButton>

                <Button
                  id="bill-factions-button"
                  aria-controls={open ? 'bill-factions-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                  onClick={handleClick}
                  variant="contained"
                  disabled={selected.length == 0}
                >
                  {t('factions')}
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  MenuListProps={{
                    'aria-labelledby': 'edit-selected',
                  }}
                  sx={{
                    textAlign: 'center',
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      handleSendPdfBillEmail(selected);
                      handleClose();
                    }}
                    disabled={
                      ButtonSendBillsIsSubmitting ||
                      selected.length == 0 ||
                      selected.some((row) => row.billingStatus !== 'GENERATED')
                    }
                  >
                    <Typography align="center" sx={{ width: '100%' }}>
                      {t('LBLButtonSendBills')}
                    </Typography>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleDownloadPdfBills(selected);
                      handleClose();
                    }}
                  >
                    <Typography align="center" sx={{ width: '100%' }}>
                      {t('LBLButtonDownloadBills')}
                    </Typography>
                  </MenuItem>
                  <MenuItem
                    style={{
                      display:
                        decodedToken?.resource_access?.admin_oli_move?.roles.some(
                          (role) => role === 'oli_move_admin'
                        )
                          ? true
                          : 'none',
                    }}
                    onClick={() => {
                      handleRegenerateBills(selected);
                      handleClose();
                    }}
                    disabled={
                      ButtonRegenerateIncorrectBillsIsSubmitting ||
                      selected.length == 0 ||
                      selected.length > 1 ||
                      selected.some(
                        (row) => row.billingStatus !== 'GENERATED'
                      ) ||
                      selected.some((row) => row.userType === 'AD_HOC')
                    }
                  >
                    <Typography align="center" sx={{ width: '100%' }}>
                      {t('LBLButtonRegenerateBills')}
                    </Typography>
                  </MenuItem>
                </Menu>

                <Suspense fallback={<div />}>
                  <p>
                    <Feedback
                      open={dataSuspenseFallbackMessage.open}
                      title={dataSuspenseFallbackMessage.title}
                      severity={dataSuspenseFallbackMessage.severity}
                      message={
                        <Typography
                          variant="subtitle1"
                          sx={{ width: 400, mx: 10 }}
                        >
                          {dataSuspenseFallbackMessage.txtMessage}
                        </Typography>
                      }
                      handleClose={() => {
                        if (dataSuspenseFallbackMessage.fetchBillsForOperator)
                          fetchBillsForDashboard();
                        setDataSuspenseFallbackMessage(
                          initDataSuspenseFallbackMessage
                        );
                      }}
                    />
                  </p>
                </Suspense>
              </Stack>
            </Form>
          )}
        </Formik>
      </Grid>
      <Divider my={6} />

      <ThemeProvider
        theme={(appTheme) => ({
          ...appTheme,
          components: {
            ...appTheme.components,
            ...dataTableTheme(appTheme).components,
          },
        })}
      >
        <Paper>
          <>
            <Paper
              variant="outlined"
              sx={{
                width: '100%',
                '& .MuiTableCell-head:not(:nth-of-type(2)) button': {
                  mx: 0,
                },
              }}
            >
              <DataTable
                data={renderData}
                columns={columns}
                options={{
                  filter: true,
                  selectableRows: 'multiple',
                  rowsSelected: selectedRows,
                  onRowSelectionChange: (_props, allRowsSelected) => {
                    setSelectedRows(allRowsSelected.map(({ index }) => index));
                  },
                  textLabels: {
                    body: {
                      noMatch: t('LBLNoBills'),
                    },
                  },
                  currentPage: currentPage,
                  totalRecords: totalRecords,
                  customFilterDialogFooter: () => {
                    return (
                      <div style={{ margin: '1rem', textAlign: 'center' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleApply}
                        >
                          {t('BTNSubmit')}
                        </Button>
                      </div>
                    );
                  },
                  onFilterDialogOpen: () => {
                    window.addEventListener('keydown', handleEnterKey);
                  },
                  onFilterDialogClose: () => {
                    window.removeEventListener('keydown', handleEnterKey);
                  },
                  onTableChange: (action, tableState) => {
                    if (action === 'filterChange') {
                      const newFilters = {};
                      tableState.filterList.forEach((filter, index) => {
                        if (filter.length > 0) {
                          newFilters[tableState.columns[index].name] = filter;
                        }
                      });
                      setNewFilters(newFilters);
                    } else if (action === 'resetFilters') {
                      setNewFilters([]);
                      resetFilterApi();
                    } else {
                    }
                  },
                }}
                getData={(seletedPageSize, currentPageVal) => {
                  setCurrentPage(currentPageVal);
                  setPageSize(seletedPageSize);
                }}
              />
            </Paper>
          </>
        </Paper>
      </ThemeProvider>
    </React.Fragment>
  );
}
export default OperatorBills;
